import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, createVNode as _createVNode, withModifiers as _withModifiers, createBlock as _createBlock, unref as _unref, vShow as _vShow, withDirectives as _withDirectives, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "f-dropdown__main" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = {
  key: 0,
  class: "f-dropdown__required"
}
const _hoisted_4 = { class: "f-dropdown__value" }
const _hoisted_5 = { class: "f-dropdown__pop" }

import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import FormDropdownCurtain from './FormDropdownCurtain.vue';
import {
  FormDropdownEmits,
  FormDropdownOption,
  FormDropdownProps,
  FormDropdownRef,
} from '@/types/form/form-dropdown/form-dropdown';
import { DynamicFormContext } from '@/types/form/dynamic-form';
import utils from '@/utils';
import services from '@/services/index';
import { ModalRef } from '@/types/elements/modal';
import useDevice from '@/hooks/useDevice';
import { FormElMsgData } from '@/types/form/others/form-el-msg';
import FormElMsg from '@/form/other/FormElMsg.vue';

const FIELD_TYPE = 'form-dropdown';

export default /*@__PURE__*/_defineComponent({
  __name: 'FormDropdown',
  props: {
    item: {},
    onChange: { type: Function }
  },
  emits: ["change"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;
const emit = __emit;
const { isMobileOrTablet } = useDevice();
const formContext = inject<DynamicFormContext>('formContext')!;
const rootEl = ref<HTMLDivElement | null>(null);
const mobileModalRef = ref<ModalRef>();
const desktopCurtainRef = ref<InstanceType<typeof FormDropdownCurtain>>();
const mobileCurtainRef = ref<InstanceType<typeof FormDropdownCurtain>>();
const focused = ref(false);
const selectedOption = ref<FormDropdownOption | undefined | null>(null);
const parsedOptions = ref<FormDropdownOption[]>([]);
const msg = ref<FormElMsgData>();
const loading = ref(false);

const isRequired = ref(props.item?.fields?.required?.value ?? false);
const isDisabled = ref(false);
const isVisible = ref(true);
const blurTimeout = ref<any>(null);

onBeforeUnmount(() => {
  if (blurTimeout.value) clearTimeout(blurTimeout.value);
});

const fields = computed(() => props.item?.fields);
const label = computed(() => fields.value?.label?.value || '');
const showLabel = computed(() => {
  const text = label.value.replace(/<[^>]*>?/gm, '').trim();
  return text.length > 0;
});
const placeholder = computed(() => fields.value?.placeholder?.value || '');
const fieldName = computed(() => fields.value?.fieldName?.value || '');
const clearable = computed(() => fields.value?.clearable?.value ?? false);
const clearText = computed(() => fields.value?.clearText?.value || placeholder.value || 'Clear');
const sourceType = computed(() => utils.sp.getVal(fields.value?.sourceType));
const isAllSelected = computed(() => {
  const text = selectedOption.value?.text?.trim() || '';
  const value = selectedOption.value?.value?.trim() || '';
  return /^all\b/i.test(text) || /^all\b/i.test(value);
});

const parseOptions = () => {
  const rawOptions = fields.value?.options;
  if (!rawOptions) {
    parsedOptions.value = [];
    return;
  }

  if (Array.isArray(rawOptions)) {
    parsedOptions.value = rawOptions.map((opt) => ({
      value: opt.fields?.data?.value || opt.fields?.data?.value || opt.id,
      text: opt.fields?.text?.value || opt.name,
    }));
    return;
  }
};

const toggleFocus = async () => {
  if (isDisabled.value) return;
  focused.value = !focused.value;
  if (!focused.value) return;

  if (isMobileOrTablet.value) {
    await mobileModalRef.value?.open();
    await nextTick();
    mobileCurtainRef.value?.focusSearch();
  } else {
    await nextTick();
    desktopCurtainRef.value?.focusSearch();
  }
};

// const onBlur = (e: any) => {
//   console.log('blur', e);
//   if (blurTimeout.value) clearTimeout(blurTimeout.value);
//   blurTimeout.value = setTimeout(() => {
//     if (!isMobileOrTablet.value) {
//       focused.value = false;
//     }
//   }, 150);
// };

const handleOptionChange = (option: FormDropdownOption | undefined, index?: number) => {
  selectedOption.value = option;
  focused.value = false;
  if (isMobileOrTablet.value) mobileModalRef.value?.close();
  msg.value = undefined;
  emit('change', FIELD_TYPE, fieldName.value, option?.value, option);
};

const handleClear = () => {
  selectedOption.value = null;
  focused.value = false;
  if (isMobileOrTablet.value) mobileModalRef.value?.close();
  msg.value = undefined;
  emit('change', FIELD_TYPE, fieldName.value, null, null);
};

const setRequired = (required: boolean) => {
  isRequired.value = required;
};

const setDisabled = (disabled: boolean) => {
  isDisabled.value = disabled;
};

const setVisible = (visible: boolean) => {
  isVisible.value = visible;
};

const updateValue = (newValue: string) => {
  if (!newValue) {
    selectedOption.value = null;
    return;
  }
  const option = parsedOptions.value.find((x) => x.value === newValue);
  selectedOption.value = option || null;
};

const updateOptions = (newOptions?: FormDropdownOption[]) => {
  parsedOptions.value = newOptions || [];
};

const validate = async () => {
  const result = {
    value: selectedOption.value?.value,
    valid: true,
    message: '',
  };

  if (!isVisible.value) {
    return result;
  }
  if (isRequired.value && !selectedOption.value) {
    msg.value = {
      type: 'error',
      message: fields.value?.requiredMsg?.value || 'This field is required',
    };
    result.valid = false;
    return result;
  }
  msg.value = undefined;
  return result;
};
const onMobileModalClosed = () => {
  focused.value = false;
};
const onBodyClick = (e: any) => {
  if (isMobileOrTablet.value) return;
  if (!rootEl.value?.contains(e.target) && rootEl.value !== e.target) {
    focused.value = false;
  }
};

const getRefData = (): FormDropdownRef => ({
  el: rootEl.value,
  type: FIELD_TYPE,
  name: fieldName.value,
  id: props.item?.id ?? '',
  visible: isVisible.value,
  setRequired,
  setDisabled,
  setVisible,
  updateValue,
  updateOptions,
  validate,
  getValue: () => selectedOption.value?.value,
});

// Register this component with parent
watch(
  () => props.item,
  () => {
    formContext.regFormEl(getRefData());
  },
  { immediate: true }
);

watch(
  () => props.item,
  () => {
    parseOptions();
    const initialValue = fields.value?.selectedOption?.fields?.data?.value;
    if (initialValue) {
      selectedOption.value = parsedOptions.value.find((o) => o.value === initialValue) || null;
    }
  },
  { immediate: true, deep: true }
);
watch(
  () => ({ isMobileOrTablet, focused }),
  () => {
    if (isMobileOrTablet.value && focused.value) {
      mobileModalRef.value?.open();
    }
  }
);
onMounted(async () => {
  formContext.regFormEl(getRefData());
  if (sourceType.value === 'country') {
    loading.value = true;
    parsedOptions.value = await services.country.getCountryOptions();
    loading.value = false;
  }
});
onMounted(() => {
  document.body.addEventListener('click', onBodyClick);
});
onBeforeUnmount(() => {
  document.body.removeEventListener('click', onBodyClick);
  formContext.unRegFormEl(getRefData());
});
__expose(getRefData());

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_modal = _resolveComponent("modal")!

  return (isVisible.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass([
      'f-dropdown',
      { focused: focused.value, disabled: isDisabled.value, 'has-error': msg.value?.type === 'error' },
    ]),
        ref_key: "rootEl",
        ref: rootEl
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", {
            class: "f-dropdown__selection",
            onClick: toggleFocus,
            tabindex: "0"
          }, [
            (showLabel.value)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(["f-dropdown__placeholder", [{ 'as-label': focused.value || !!selectedOption.value }]])
                }, [
                  _createElementVNode("span", {
                    class: "f-dropdown__label",
                    innerHTML: label.value
                  }, null, 8, _hoisted_2),
                  (isRequired.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_3, "*"))
                    : _createCommentVNode("", true)
                ], 2))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_4, _toDisplayString(selectedOption.value?.text), 1),
            _createVNode(_component_Icon, {
              name: "down",
              size: "xxs",
              class: "f-dropdown__icon"
            }),
            (selectedOption.value && !isAllSelected.value)
              ? (_openBlock(), _createBlock(_component_Icon, {
                  key: 1,
                  name: "close",
                  size: "xxs",
                  class: "f-dropdown__clear",
                  onClick: _withModifiers(handleClear, ["stop"])
                }))
              : _createCommentVNode("", true)
          ]),
          _withDirectives(_createElementVNode("div", _hoisted_5, [
            _createVNode(FormDropdownCurtain, {
              ref_key: "desktopCurtainRef",
              ref: desktopCurtainRef,
              options: parsedOptions.value,
              loading: loading.value,
              selectedOption: selectedOption.value,
              clearable: clearable.value,
              clearText: clearText.value,
              onOptionClick: handleOptionChange,
              onClear: handleClear
            }, null, 8, ["options", "loading", "selectedOption", "clearable", "clearText"])
          ], 512), [
            [_vShow, !_unref(isMobileOrTablet) && focused.value]
          ])
        ]),
        _createVNode(FormElMsg, { msg: msg.value }, null, 8, ["msg"]),
        _createVNode(_component_modal, {
          class: "f-dropdown__mobile-modal",
          animation: "bottom-slide-in",
          closable: "",
          sticky: false,
          ref_key: "mobileModalRef",
          ref: mobileModalRef,
          onClosed: onMobileModalClosed
        }, {
          default: _withCtx(() => [
            _createVNode(FormDropdownCurtain, {
              ref_key: "mobileCurtainRef",
              ref: mobileCurtainRef,
              options: parsedOptions.value,
              loading: loading.value,
              selectedOption: selectedOption.value,
              clearable: clearable.value,
              clearText: clearText.value,
              onOptionClick: handleOptionChange,
              onClear: handleClear
            }, null, 8, ["options", "loading", "selectedOption", "clearable", "clearText"])
          ]),
          _: 1
        }, 512)
      ], 2))
    : _createCommentVNode("", true)
}
}

})